import React, { useState, useEffect } from 'react';
import axiosInstance from '../utils/axiosConfig';
import { toast } from 'react-toastify';
import {
    Plus, Save, Trash2, Edit2, X, ChevronDown, ChevronUp,
    ToggleLeft, ToggleRight, Copy, ListChecks, AlertCircle
} from 'lucide-react';

// ─── Tipler ───────────────────────────────────────────────────────────────────

interface SablonDetay {
    detayId?: string;
    odemeTuru: string;
    vadeAyEndeksi: number;
    hesaplamaYontemi: string;
    deger: number;
}

interface Sablon {
    sablonId: string;
    sablonAdi: string;
    pesinatTipi: string;
    pesinatDegeri: number;
    vadeTipi: string;
    maksimumTaksitSayisi: number;
    kalanBakiyeDagilimTipi: string;
    kurusYuvarlamaTipi: string;
    aciklama?: string;
    isActive: boolean;
    createdDate: string;
    detaylar: SablonDetay[];
}

const BOSH_DETAY: SablonDetay = {
    odemeTuru: 'AraOdeme',
    vadeAyEndeksi: 0,
    hesaplamaYontemi: 'ToplaminYuzdesi',
    deger: 0,
};

const BOSH_FORM: Omit<Sablon, 'sablonId' | 'createdDate'> = {
    sablonAdi: '',
    pesinatTipi: 'Oran',
    pesinatDegeri: 30,
    vadeTipi: 'EsitTaksit',
    maksimumTaksitSayisi: 12,
    kalanBakiyeDagilimTipi: 'KalanEsit',
    kurusYuvarlamaTipi: 'IlkTaksit',
    aciklama: '',
    isActive: true,
    detaylar: [],
};

// ─── Yardımcı ─────────────────────────────────────────────────────────────────

const inp: React.CSSProperties = {
    width: '100%', padding: '8px 12px', borderRadius: 8,
    border: '1px solid #d1d5db', fontSize: '0.875rem',
    outline: 'none', boxSizing: 'border-box', background: '#fff', color: '#111827',
};

const lbl: React.CSSProperties = {
    display: 'block', marginBottom: 5, fontSize: '0.75rem',
    fontWeight: 600, color: '#374151',
};

const PESINAT_TIPI = ['Oran', 'SabitTutar'];
const VADE_TIPI = ['EsitTaksit', 'AraOdemeli', 'SerbestOdeme'];
const KALAN_DAGILIM = ['KalanEsit', 'YuzdeselDagilim'];
const KURUS_YUVARLAMA = ['IlkTaksit', 'SonTaksit'];
const ODEME_TURU = ['Pesinat', 'Taksit', 'AraOdeme', 'Teslimat'];
const HESAPLAMA_YONTEMI = ['KalaninYuzdesi', 'ToplaminYuzdesi', 'SabitTutar'];

const labelFor: Record<string, string> = {
    Oran: 'Oran (%)', SabitTutar: 'Sabit Tutar (₺)',
    EsitTaksit: 'Eşit Taksit', AraOdemeli: 'Ara Ödemeli', SerbestOdeme: 'Serbest Ödeme',
    KalanEsit: 'Kalan Eşit', YuzdeselDagilim: 'Yüzdesel Dağılım',
    IlkTaksit: 'İlk Taksit', SonTaksit: 'Son Taksit',
    Pesinat: 'Peşinat', Taksit: 'Taksit', AraOdeme: 'Ara Ödeme', Teslimat: 'Teslimat',
    KalaninYuzdesi: 'Kalanın Yüzdesi', ToplaminYuzdesi: 'Toplamın Yüzdesi',
};

// ─── Bileşen ──────────────────────────────────────────────────────────────────

const OdemePlanSablonlari: React.FC = () => {
    const [sablonlar, setSablonlar] = useState<Sablon[]>([]);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [form, setForm] = useState<Omit<Sablon, 'sablonId' | 'createdDate'>>(BOSH_FORM);
    const [saving, setSaving] = useState(false);
    const [expandedId, setExpandedId] = useState<string | null>(null);
    const [deletingId, setDeletingId] = useState<string | null>(null);

    useEffect(() => { fetchSablonlar(); }, []);

    const fetchSablonlar = async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get('/api/OdemePlanSablonlari');
            setSablonlar(res.data);
        } catch {
            toast.error('Şablonlar yüklenemedi');
        } finally {
            setLoading(false);
        }
    };

    const handleNew = () => {
        setForm({ ...BOSH_FORM, detaylar: [] });
        setEditingId(null);
        setShowForm(true);
    };

    const handleEdit = (s: Sablon) => {
        setForm({
            sablonAdi: s.sablonAdi,
            pesinatTipi: s.pesinatTipi,
            pesinatDegeri: s.pesinatDegeri,
            vadeTipi: s.vadeTipi,
            maksimumTaksitSayisi: s.maksimumTaksitSayisi,
            kalanBakiyeDagilimTipi: s.kalanBakiyeDagilimTipi,
            kurusYuvarlamaTipi: s.kurusYuvarlamaTipi,
            aciklama: s.aciklama || '',
            isActive: s.isActive,
            detaylar: s.detaylar.map(d => ({ ...d })),
        });
        setEditingId(s.sablonId);
        setShowForm(true);
    };

    const handleClone = (s: Sablon) => {
        setForm({
            sablonAdi: `${s.sablonAdi} (Kopya)`,
            pesinatTipi: s.pesinatTipi,
            pesinatDegeri: s.pesinatDegeri,
            vadeTipi: s.vadeTipi,
            maksimumTaksitSayisi: s.maksimumTaksitSayisi,
            kalanBakiyeDagilimTipi: s.kalanBakiyeDagilimTipi,
            kurusYuvarlamaTipi: s.kurusYuvarlamaTipi,
            aciklama: s.aciklama || '',
            isActive: true,
            detaylar: s.detaylar.map(d => ({ ...d, detayId: undefined })),
        });
        setEditingId(null);
        setShowForm(true);
    };

    const handleSave = async () => {
        if (!form.sablonAdi.trim()) { toast.warning('Şablon adı zorunludur'); return; }
        setSaving(true);
        try {
            if (editingId) {
                await axiosInstance.put(`/api/OdemePlanSablonlari/${editingId}`, form);
                toast.success('Şablon güncellendi');
            } else {
                await axiosInstance.post('/api/OdemePlanSablonlari', form);
                toast.success('Şablon oluşturuldu');
            }
            setShowForm(false);
            fetchSablonlar();
        } catch {
            toast.error('Kayıt sırasında hata oluştu');
        } finally {
            setSaving(false);
        }
    };

    const handleToggleActive = async (s: Sablon) => {
        try {
            await axiosInstance.patch(`/api/OdemePlanSablonlari/${s.sablonId}/toggle-active`);
            fetchSablonlar();
        } catch { toast.error('Durum güncellenemedi'); }
    };

    const handleDelete = async (id: string) => {
        setDeletingId(id);
        try {
            await axiosInstance.delete(`/api/OdemePlanSablonlari/${id}`);
            toast.success('Şablon silindi');
            fetchSablonlar();
        } catch { toast.error('Şablon silinirken hata oluştu'); }
        finally { setDeletingId(null); }
    };

    // Detay helpers
    const addDetay = () => setForm(f => ({ ...f, detaylar: [...f.detaylar, { ...BOSH_DETAY }] }));
    const updateDetay = (idx: number, field: keyof SablonDetay, val: string | number) =>
        setForm(f => ({ ...f, detaylar: f.detaylar.map((d, i) => i === idx ? { ...d, [field]: val } : d) }));
    const removeDetay = (idx: number) =>
        setForm(f => ({ ...f, detaylar: f.detaylar.filter((_, i) => i !== idx) }));

    // ─── Render ───────────────────────────────────────────────────────────────

    return (
        <div style={{ fontFamily: "'Inter','Segoe UI',sans-serif" }}>
            {/* Başlık */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <div>
                    <h3 style={{ margin: 0, fontWeight: 700, fontSize: '1.05rem', color: '#111827', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <ListChecks size={18} color="#cd6906" /> Ödeme Planı Şablonları
                    </h3>
                    <p style={{ margin: '4px 0 0', fontSize: '0.8125rem', color: '#6b7280' }}>
                        Daire satışlarında kullanılacak peşinat ve taksit kurallarını tanımlayın.
                    </p>
                </div>
                {!showForm && (
                    <button onClick={handleNew} style={{
                        background: '#cd6906', color: '#fff', border: 'none', borderRadius: 8,
                        padding: '8px 16px', fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem',
                        display: 'flex', alignItems: 'center', gap: 6,
                    }}>
                        <Plus size={14} /> Yeni Şablon
                    </button>
                )}
            </div>

            {/* Form */}
            {showForm && (
                <div style={{
                    background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12,
                    padding: '24px', marginBottom: 20,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                        <h4 style={{ margin: 0, fontWeight: 700, color: '#111827', fontSize: '0.975rem' }}>
                            {editingId ? 'Şablonu Düzenle' : 'Yeni Şablon'}
                        </h4>
                        <button onClick={() => setShowForm(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: 4 }}>
                            <X size={18} />
                        </button>
                    </div>

                    {/* Temel Bilgiler */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
                        <div style={{ gridColumn: '1 / -1' }}>
                            <label style={lbl}>Şablon Adı *</label>
                            <input style={inp} value={form.sablonAdi} onChange={e => setForm(f => ({ ...f, sablonAdi: e.target.value }))} placeholder="Örn: Standart %30 Peşinat, 12 Taksit" />
                        </div>
                        <div>
                            <label style={lbl}>Peşinat Tipi</label>
                            <select style={inp} value={form.pesinatTipi} onChange={e => setForm(f => ({ ...f, pesinatTipi: e.target.value }))}>
                                {PESINAT_TIPI.map(v => <option key={v} value={v}>{labelFor[v] ?? v}</option>)}
                            </select>
                        </div>
                        <div>
                            <label style={lbl}>{form.pesinatTipi === 'Oran' ? 'Peşinat Oranı (%)' : 'Peşinat Tutarı (₺)'}</label>
                            <input style={inp} type="number" min="0" value={form.pesinatDegeri}
                                onChange={e => setForm(f => ({ ...f, pesinatDegeri: parseFloat(e.target.value) || 0 }))} />
                        </div>
                        <div>
                            <label style={lbl}>Vade Tipi</label>
                            <select style={inp} value={form.vadeTipi} onChange={e => setForm(f => ({ ...f, vadeTipi: e.target.value }))}>
                                {VADE_TIPI.map(v => <option key={v} value={v}>{labelFor[v] ?? v}</option>)}
                            </select>
                        </div>
                        <div>
                            <label style={lbl}>Maks. Taksit Sayısı</label>
                            <input style={inp} type="number" min="1" max="360" value={form.maksimumTaksitSayisi}
                                onChange={e => setForm(f => ({ ...f, maksimumTaksitSayisi: parseInt(e.target.value) || 1 }))} />
                        </div>
                        <div>
                            <label style={lbl}>Kalan Bakiye Dağılımı</label>
                            <select style={inp} value={form.kalanBakiyeDagilimTipi} onChange={e => setForm(f => ({ ...f, kalanBakiyeDagilimTipi: e.target.value }))}>
                                {KALAN_DAGILIM.map(v => <option key={v} value={v}>{labelFor[v] ?? v}</option>)}
                            </select>
                        </div>
                        <div>
                            <label style={lbl}>Kuruş Yuvarlama</label>
                            <select style={inp} value={form.kurusYuvarlamaTipi} onChange={e => setForm(f => ({ ...f, kurusYuvarlamaTipi: e.target.value }))}>
                                {KURUS_YUVARLAMA.map(v => <option key={v} value={v}>{labelFor[v] ?? v}</option>)}
                            </select>
                        </div>
                        <div style={{ gridColumn: '1 / -1' }}>
                            <label style={lbl}>Açıklama</label>
                            <textarea style={{ ...inp, resize: 'vertical', minHeight: 60, fontFamily: 'inherit' }}
                                value={form.aciklama || ''} onChange={e => setForm(f => ({ ...f, aciklama: e.target.value }))}
                                placeholder="İsteğe bağlı açıklama" />
                        </div>
                    </div>

                    {/* Ara Ödemeler / Detaylar */}
                    <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: 16, marginBottom: 16 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                            <span style={{ fontWeight: 600, fontSize: '0.875rem', color: '#374151' }}>
                                Ödeme Detayları (Ara Ödemeler / Özel Kalemler)
                            </span>
                            <button onClick={addDetay} style={{
                                background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 7,
                                padding: '5px 12px', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600,
                                color: '#374151', display: 'flex', alignItems: 'center', gap: 5,
                            }}>
                                <Plus size={12} /> Satır Ekle
                            </button>
                        </div>

                        {form.detaylar.length === 0 ? (
                            <div style={{
                                textAlign: 'center', padding: '16px', background: '#f9fafb',
                                borderRadius: 8, border: '1.5px dashed #e5e7eb',
                                color: '#9ca3af', fontSize: '0.8125rem',
                            }}>
                                Henüz ödeme detayı yok. Peşinat dışında özel kalemleri buraya ekleyebilirsiniz.
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {/* Tablo başlığı */}
                                <div style={{
                                    display: 'grid', gridTemplateColumns: '1.5fr 90px 1.5fr 100px 36px',
                                    gap: 8, padding: '0 4px',
                                }}>
                                    {['Ödeme Türü', 'Vade (Ay)', 'Hesaplama Yöntemi', 'Değer', ''].map(h => (
                                        <span key={h} style={{ fontSize: '0.7rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</span>
                                    ))}
                                </div>
                                {form.detaylar.map((d, idx) => (
                                    <div key={idx} style={{
                                        display: 'grid', gridTemplateColumns: '1.5fr 90px 1.5fr 100px 36px',
                                        gap: 8, alignItems: 'center',
                                        background: '#f9fafb', borderRadius: 8, padding: '8px',
                                        border: '1px solid #e5e7eb',
                                    }}>
                                        <select style={{ ...inp, padding: '6px 10px' }} value={d.odemeTuru}
                                            onChange={e => updateDetay(idx, 'odemeTuru', e.target.value)}>
                                            {ODEME_TURU.map(v => <option key={v} value={v}>{labelFor[v] ?? v}</option>)}
                                        </select>
                                        <input style={{ ...inp, padding: '6px 10px' }} type="number" min="0" value={d.vadeAyEndeksi}
                                            onChange={e => updateDetay(idx, 'vadeAyEndeksi', parseInt(e.target.value) || 0)} />
                                        <select style={{ ...inp, padding: '6px 10px' }} value={d.hesaplamaYontemi}
                                            onChange={e => updateDetay(idx, 'hesaplamaYontemi', e.target.value)}>
                                            {HESAPLAMA_YONTEMI.map(v => <option key={v} value={v}>{labelFor[v] ?? v}</option>)}
                                        </select>
                                        <input style={{ ...inp, padding: '6px 10px' }} type="number" min="0" step="0.01" value={d.deger}
                                            onChange={e => updateDetay(idx, 'deger', parseFloat(e.target.value) || 0)} />
                                        <button onClick={() => removeDetay(idx)} style={{
                                            background: 'none', border: 'none', cursor: 'pointer',
                                            color: '#ef4444', padding: 4, borderRadius: 6,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        }}>
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Kaydet */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                        <button onClick={() => setShowForm(false)} style={{
                            background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 8,
                            padding: '8px 18px', fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem', color: '#374151',
                        }}>
                            İptal
                        </button>
                        <button onClick={handleSave} disabled={saving} style={{
                            background: saving ? '#e5e7eb' : '#cd6906', color: saving ? '#9ca3af' : '#fff',
                            border: 'none', borderRadius: 8, padding: '8px 22px',
                            fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer', fontSize: '0.85rem',
                            display: 'flex', alignItems: 'center', gap: 6,
                        }}>
                            <Save size={14} /> {saving ? 'Kaydediliyor...' : editingId ? 'Güncelle' : 'Kaydet'}
                        </button>
                    </div>
                </div>
            )}

            {/* Liste */}
            {loading ? (
                <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>Yükleniyor...</div>
            ) : sablonlar.length === 0 ? (
                <div style={{
                    textAlign: 'center', padding: 48, background: '#f9fafb',
                    borderRadius: 12, border: '2px dashed #e5e7eb', color: '#9ca3af',
                }}>
                    <AlertCircle size={36} color="#d1d5db" style={{ marginBottom: 10 }} />
                    <p style={{ margin: 0, fontWeight: 600, color: '#6b7280' }}>Henüz ödeme planı şablonu yok</p>
                    <p style={{ margin: '4px 0 0', fontSize: '0.8125rem' }}>Yeni Şablon butonuyla ilk şablonu oluşturun.</p>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {sablonlar.map(s => {
                        const expanded = expandedId === s.sablonId;
                        return (
                            <div key={s.sablonId} style={{
                                background: '#fff', borderRadius: 12,
                                border: `1px solid ${s.isActive ? '#e5e7eb' : '#f3f4f6'}`,
                                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                                opacity: s.isActive ? 1 : 0.6,
                                overflow: 'hidden',
                            }}>
                                {/* Başlık satırı */}
                                <div style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    padding: '14px 18px', gap: 12,
                                }}>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                            <span style={{ fontWeight: 700, color: '#111827', fontSize: '0.9375rem' }}>
                                                {s.sablonAdi}
                                            </span>
                                            {!s.isActive && (
                                                <span style={{ background: '#f3f4f6', color: '#9ca3af', fontSize: '0.7rem', fontWeight: 700, padding: '2px 8px', borderRadius: 20 }}>
                                                    PASİF
                                                </span>
                                            )}
                                        </div>
                                        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 6 }}>
                                            <Chip label="Peşinat" value={s.pesinatTipi === 'Oran' ? `%${s.pesinatDegeri}` : `₺${s.pesinatDegeri.toLocaleString('tr-TR')}`} />
                                            <Chip label="Vade" value={labelFor[s.vadeTipi] ?? s.vadeTipi} />
                                            <Chip label="Maks. Taksit" value={`${s.maksimumTaksitSayisi} ay`} />
                                            <Chip label="Kuruş" value={labelFor[s.kurusYuvarlamaTipi] ?? s.kurusYuvarlamaTipi} />
                                            {s.detaylar.length > 0 && (
                                                <Chip label="Ara Ödeme" value={`${s.detaylar.length} kalem`} accent />
                                            )}
                                        </div>
                                    </div>
                                    {/* Aksiyonlar */}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                                        <button onClick={() => handleToggleActive(s)} title={s.isActive ? 'Pasife Al' : 'Aktife Al'}
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 5, borderRadius: 7, color: s.isActive ? '#10b981' : '#9ca3af' }}>
                                            {s.isActive ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
                                        </button>
                                        <button onClick={() => handleClone(s)} title="Kopyala"
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 5, borderRadius: 7, color: '#6b7280' }}>
                                            <Copy size={15} />
                                        </button>
                                        <button onClick={() => handleEdit(s)} title="Düzenle"
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 5, borderRadius: 7, color: '#6b7280' }}>
                                            <Edit2 size={15} />
                                        </button>
                                        <button onClick={() => {
                                            if (window.confirm(`"${s.sablonAdi}" şablonunu silmek istediğinize emin misiniz?`)) handleDelete(s.sablonId);
                                        }} disabled={deletingId === s.sablonId} title="Sil"
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 5, borderRadius: 7, color: '#ef4444' }}>
                                            <Trash2 size={15} />
                                        </button>
                                        {s.detaylar.length > 0 && (
                                            <button onClick={() => setExpandedId(expanded ? null : s.sablonId)}
                                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 5, borderRadius: 7, color: '#6b7280' }}>
                                                {expanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Detay Tablosu */}
                                {expanded && s.detaylar.length > 0 && (
                                    <div style={{ borderTop: '1px solid #f3f4f6', padding: '12px 18px 14px' }}>
                                        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: '0.8125rem' }}>
                                            <thead>
                                                <tr style={{ background: '#f9fafb' }}>
                                                    {['Ödeme Türü', 'Vade (Ay)', 'Hesaplama Yöntemi', 'Değer'].map(h => (
                                                        <th key={h} style={{ padding: '7px 12px', textAlign: 'left', fontWeight: 700, color: '#6b7280', borderBottom: '1px solid #e5e7eb', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {s.detaylar.map((d, i) => (
                                                    <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                                                        <td style={{ padding: '8px 12px', borderBottom: '1px solid #f3f4f6', color: '#374151', fontWeight: 600 }}>
                                                            {labelFor[d.odemeTuru] ?? d.odemeTuru}
                                                        </td>
                                                        <td style={{ padding: '8px 12px', borderBottom: '1px solid #f3f4f6', color: '#374151' }}>
                                                            {d.vadeAyEndeksi} ay
                                                        </td>
                                                        <td style={{ padding: '8px 12px', borderBottom: '1px solid #f3f4f6', color: '#374151' }}>
                                                            {labelFor[d.hesaplamaYontemi] ?? d.hesaplamaYontemi}
                                                        </td>
                                                        <td style={{ padding: '8px 12px', borderBottom: '1px solid #f3f4f6', color: '#374151', fontWeight: 600 }}>
                                                            {d.hesaplamaYontemi === 'SabitTutar'
                                                                ? `₺${d.deger.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`
                                                                : `%${d.deger}`}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                                {s.aciklama && (
                                    <div style={{ padding: '0 18px 12px', fontSize: '0.8rem', color: '#6b7280', fontStyle: 'italic' }}>
                                        {s.aciklama}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

// Küçük chip yardımcısı
const Chip: React.FC<{ label: string; value: string; accent?: boolean }> = ({ label, value, accent }) => (
    <span style={{ fontSize: '0.7375rem', color: accent ? '#92400e' : '#374151', display: 'inline-flex', gap: 4 }}>
        <span style={{ color: '#9ca3af', fontWeight: 500 }}>{label}:</span>
        <span style={{ fontWeight: 700, color: accent ? '#d97706' : '#1f2937' }}>{value}</span>
    </span>
);

export default OdemePlanSablonlari;
